/**
 * Manufacturer Selector Component
 * Dropdown component for selecting manufacturers with search functionality
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigation } from '../contexts/NavigationContext';

export interface ManufacturerDetails {
  id: string;
  name: string;
  email: string;
  status: 'approved' | 'pending' | 'rejected';
  stats: {
    totalProducts: number;
    totalBatches: number;
    totalUsers: number;
  };
}

interface ManufacturerSelectorProps {
  manufacturers: ManufacturerDetails[];
  onManufacturerSelect?: (manufacturer: ManufacturerDetails | null) => void;
  showAllOption?: boolean;
  loading?: boolean;
  className?: string;
  placeholder?: string;
}

export const ManufacturerSelector: React.FC<ManufacturerSelectorProps> = ({
  manufacturers,
  onManufacturerSelect,
  showAllOption = true,
  loading = false,
  className = '',
  placeholder = 'Select manufacturer...'
}) => {
  const { selectedManufacturer, setSelectedManufacturer } = useNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredManufacturers, setFilteredManufacturers] = useState<ManufacturerDetails[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter manufacturers based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = manufacturers.filter(manufacturer =>
        manufacturer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        manufacturer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredManufacturers(filtered);
    } else {
      setFilteredManufacturers(manufacturers);
    }
  }, [manufacturers, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleManufacturerSelect = (manufacturer: ManufacturerDetails | null) => {
    setSelectedManufacturer(manufacturer?.id || null);
    setIsOpen(false);
    setSearchTerm('');
    onManufacturerSelect?.(manufacturer);
  };

  const getSelectedManufacturerName = () => {
    if (!selectedManufacturer) {
      return showAllOption ? 'All Manufacturers' : placeholder;
    }
    const manufacturer = manufacturers.find(m => m.id === selectedManufacturer);
    return manufacturer?.name || 'Unknown Manufacturer';
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm cursor-not-allowed opacity-50">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Loading manufacturers...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        className="relative w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="block truncate">{getSelectedManufacturerName()}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search manufacturers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Options List */}
          <div className="max-h-80 overflow-y-auto">
            {/* All Manufacturers Option */}
            {showAllOption && (
              <button
                type="button"
                className={`w-full px-3 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                  !selectedManufacturer ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                }`}
                onClick={() => handleManufacturerSelect(null)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">All Manufacturers</div>
                    <div className="text-sm text-gray-500">System-wide analytics</div>
                  </div>
                  {!selectedManufacturer && (
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            )}

            {/* Manufacturer Options */}
            {filteredManufacturers.length > 0 ? (
              filteredManufacturers.map((manufacturer) => (
                <button
                  key={manufacturer.id}
                  type="button"
                  className={`w-full px-3 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                    selectedManufacturer === manufacturer.id ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                  }`}
                  onClick={() => handleManufacturerSelect(manufacturer)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="font-medium truncate">{manufacturer.name}</div>
                        <span
                          className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            manufacturer.status
                          )}`}
                        >
                          {manufacturer.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 truncate">{manufacturer.email}</div>
                      <div className="flex items-center mt-1 text-xs text-gray-400">
                        <span>{manufacturer.stats.totalProducts} products</span>
                        <span className="mx-1">•</span>
                        <span>{manufacturer.stats.totalBatches} batches</span>
                        <span className="mx-1">•</span>
                        <span>{manufacturer.stats.totalUsers} users</span>
                      </div>
                    </div>
                    {selectedManufacturer === manufacturer.id && (
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500">
                {searchTerm ? 'No manufacturers found matching your search.' : 'No manufacturers available.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManufacturerSelector;